import { Link } from 'react-router';
import { BonusCodeButton } from '@/components/ui/BonusCodeButton';
import { DemoBetPanel } from './moon/DemoBetPanel';
import { HowItWorks, type Step } from './moon/HowItWorks';
import { JoinCard } from './moon/JoinCard';
import { MarketsCard } from './moon/MarketsCard';
import { CoinMark } from './moon/CoinMark';
import { formatPercent, formatPrice, useQuotes } from './moon/markets';
import './moon/moon.css';

/**
 * Moon's campaign page.
 *
 * A standalone page with its copy in this file, at the owner's instruction —
 * unlike a partner site, which lives in catalog and is edited from the panel.
 * The trade is deliberate and worth restating: every word below changes only
 * with a code change and a deploy.
 *
 * Everything editorial is in ONE block at the top. If this page grows a second
 * campaign, or the copy starts changing weekly, that is the signal to move it
 * into catalog and delete this.
 */
const moon = {
  /**
   * Where the button sends people.
   *
   * Through the shortener rather than straight to the operator, so the click is
   * counted and the destination can be changed without touching this file —
   * which is the one thing here that must be changeable in a hurry when an
   * operator moves domain, as Stake just did.
   */
  clickUrl: 'https://gee.direct/s/moon',

  /**
   * Moon's own violet, sampled from the flag in the hero artwork rather than
   * picked by eye. A partner's accent that is nearly-but-not-quite theirs looks
   * like a mistake in a way an obviously different colour does not.
   */
  accent: '#aea2e5',

  hero: {
    partnership: 'Resmi İşbirliği',
    /** Split so the second half can carry the accent. One sentence, two colours. */
    headline: 'Piyasanın yönü.',
    headlineAccent: 'Senin bakış açın.',
    description:
      'Moon, kripto ve hisselerde fiyatın kısa vadeli yönü üzerine bahis oynayabileceğin kaldıraçlı tahmin platformudur.',
  },

  /**
   * First click to first position.
   *
   * `duration` is a claim about Moon's own onboarding, not something measured
   * here — it sits in this block so it can be corrected without hunting through
   * a component.
   */
  howItWorks: {
    duration: '3–5 dakika',
    steps: [
      {
        icon: 'register',
        title: 'Kayıt Ol',
        body: 'Referans linkimizle Moon’a git ve hesabını oluştur. Referans kodu otomatik eklenir.',
      },
      {
        icon: 'deposit',
        title: 'Bakiye Yükle',
        body: 'USDT, BTC, ETH veya SOL ağları üzerinden cüzdanından transfer yap. Onay sonrası bakiye anında yansır.',
      },
      {
        icon: 'direction',
        title: 'Yönünü Seç',
        body: 'Bir varlık seç, tutarını ve kaldıracını belirle. Yukarı veya Aşağı yönüne karar ver.',
      },
      {
        icon: 'track',
        title: 'Pozisyonu Takip Et',
        body: 'Canlı akıştan kâr/zararını ve tasfiye seviyeni anlık olarak takip et.',
      },
    ] satisfies Step[],
  },

  /** The headline offer. Free text: no field adds it up and nothing verifies it. */
  bonus: {
    headline: '',
    subline: '',
    code: '',
  },

  /**
   * Selling points. Editorial and unverified — worded as our opinion, never as
   * though somebody audited the operator.
   */
  highlights: [] as { text: string; icon: HighlightIcon }[],

  terms: '',
} as const;

type HighlightIcon = 'check' | 'withdrawal' | 'games' | 'vip' | 'support' | 'bonus';

export function MoonPage() {
  const hasBonus = moon.bonus.headline !== '';

  return (
    <div className="moon-surface moon-page py-2">
      <Hero />

      <section id="moon-terminal" className="moon-terminal" aria-labelledby="moon-terminal-title">
        <div className="moon-section-heading">
          <div>
            <p className="moon-eyebrow">Piyasayı keşfet</p>
            <h2 id="moon-terminal-title">Kendi senaryonu oluştur.</h2>
          </div>
          <p>
            Canlı fiyatları takip et. Tutar ve kaldıraç değiştiğinde hesaplamanın nasıl değiştiğini
            gör.
          </p>
        </div>
        <div className="moon-terminal-grid">
          <DemoBetPanel clickUrl={moon.clickUrl} accent={moon.accent} />
          <MarketsCard />
        </div>
      </section>

      <HowItWorks
        steps={moon.howItWorks.steps}
        duration={moon.howItWorks.duration}
        accent={moon.accent}
      />
      <JoinCard clickUrl={moon.clickUrl} accent={moon.accent} />

      {hasBonus || moon.bonus.code ? (
        <section className="mt-4 flex flex-col items-center gap-5 rounded-[var(--radius-card)] border border-surface-800 bg-surface-900/80 px-5 py-8 text-center sm:px-8">
          {hasBonus ? (
            <div>
              <p className="text-2xl font-extrabold uppercase leading-tight tracking-tight text-surface-50 sm:text-3xl">
                {moon.bonus.headline}
              </p>
              {moon.bonus.subline ? (
                <p className="mt-1 text-lg font-semibold sm:text-xl" style={{ color: moon.accent }}>
                  {moon.bonus.subline}
                </p>
              ) : null}
            </div>
          ) : null}

          {moon.bonus.code ? (
            <div className="w-full max-w-xs">
              <p className="mb-1 text-xs font-medium uppercase tracking-widest text-surface-500">
                Özel kod
              </p>
              <BonusCodeButton code={moon.bonus.code} />
            </div>
          ) : null}
        </section>
      ) : null}

      {moon.highlights.length > 0 ? (
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {moon.highlights.map((highlight) => (
            <li
              key={highlight.text}
              className="flex items-center gap-3 rounded-xl border border-surface-800 bg-surface-900/60 px-4 py-3 text-sm text-surface-300"
            >
              <span style={{ color: moon.accent }}>
                <HighlightGlyph name={highlight.icon} />
              </span>
              {highlight.text}
            </li>
          ))}
        </ul>
      ) : null}

      {moon.terms ? (
        <details className="group mt-4 rounded-xl border border-surface-800 bg-surface-900/60 px-4 py-3">
          <summary className="cursor-pointer list-none text-sm text-surface-400 transition hover:text-surface-200">
            Şartlar ve koşullar
            <span aria-hidden className="ml-1 inline-block transition group-open:rotate-90">
              ›
            </span>
          </summary>
          <p className="mt-2 whitespace-pre-line text-xs leading-relaxed text-surface-500">
            {moon.terms}
          </p>
        </details>
      ) : null}

      <div className="moon-backlink">
        <Link
          to="/trusted-sites"
          className="text-xs text-surface-500 underline-offset-4 transition hover:text-surface-300 hover:underline"
        >
          Diğer güvenilir siteler
        </Link>
      </div>

      <p className="moon-disclosure">
        18+. Kaldıraçlı işlemler yüksek risk taşır ve yatırdığın tutarın tamamını kaybedebilirsin.
        Sorumlu oyna. Bu sayfadaki bağlantı üzerinden yapılan kayıtlardan komisyon kazanabiliriz.
      </p>
    </div>
  );
}

function Hero() {
  const { data: quotes } = useQuotes();
  const bitcoin = quotes?.find((quote) => quote.symbol === 'BTCUSDT');

  return (
    <section aria-labelledby="moon-hero" className="moon-hero moon-mission">
      <div aria-hidden="true" className="moon-mission-art">
        <img src="/moonhero.webp" alt="" width={1528} height={696} fetchPriority="high" />
      </div>
      <div aria-hidden="true" className="moon-mission-shade" />
      <div className="moon-mission-copy">
        <div className="moon-partnership">
          <img
            src="/moon-logo.svg"
            alt="Moon"
            width={407}
            height={61}
            fetchPriority="high"
            className="moon-partner-logo"
          />
          <span aria-hidden="true" className="moon-partner-times">
            ×
          </span>
          <img
            src="/gee-logo.png"
            alt="GeeFamily"
            width={881}
            height={519}
            className="moon-gee-logo"
          />
          <span className="moon-partner-label">{moon.hero.partnership}</span>
        </div>
        <h1 id="moon-hero">
          {moon.hero.headline}
          <span>{moon.hero.headlineAccent}</span>
        </h1>
        <p className="moon-mission-description">{moon.hero.description}</p>
        <div className="moon-mission-actions">
          <a href="#moon-terminal" className="moon-button moon-button-primary">
            Demoyu keşfet <span aria-hidden="true">↓</span>
          </a>
          <a
            href={moon.clickUrl}
            target="_blank"
            rel="noreferrer noopener sponsored"
            className="moon-button moon-button-glass"
          >
            Moon’a git <span aria-hidden="true">↗</span>
          </a>
        </div>
        <p className="moon-mission-caption">
          <span aria-hidden="true" />
          Demo panelinde gerçek işlem yapılmaz.
        </p>
      </div>
      {bitcoin ? (
        <div className="moon-signal" data-tilt>
          <div className="moon-signal-top">
            <CoinMark code="BTC" className="size-8" />
            <span>
              Bitcoin <span className="text-surface-500">/ USDT</span>
            </span>
          </div>
          <p className="moon-signal-price">{formatPrice(bitcoin.price)}</p>
          <div className="moon-signal-bottom">
            <span>Binance · 24 sa</span>
            {bitcoin ? (
              <span className={bitcoin.changePercent >= 0 ? 'text-emerald-300' : 'text-rose-300'}>
                {formatPercent(bitcoin.changePercent)}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
      <span aria-hidden="true" className="moon-mission-signature">
        TOGETHER WE TRADE
      </span>
    </section>
  );
}

function HighlightGlyph({ name }: { name: HighlightIcon }) {
  const paths: Record<HighlightIcon, string> = {
    check: 'M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z',
    withdrawal: 'M12 2 8 6h3v8h2V6h3l-4-4zM4 18v2h16v-2H4z',
    games:
      'M17 8H7a5 5 0 0 0 0 10h10a5 5 0 0 0 0-10zM9 14H7v2H5v-2H3v-2h2v-2h2v2h2v2zm6 2a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm3-3a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z',
    vip: 'M3 8l4 3 5-6 5 6 4-3-2 11H5L3 8z',
    support:
      'M12 2a9 9 0 0 0-9 9v5a3 3 0 0 0 3 3h1v-8H5a7 7 0 0 1 14 0h-2v8h4v2h-4v-2a3 3 0 0 0 3-3v-5a9 9 0 0 0-9-9z',
    bonus:
      'M20 7h-2.2a3 3 0 0 0-5-3.3L12 4.5l-.8-.8A3 3 0 0 0 6.2 7H4v4h1v9h14v-9h1V7zm-7 11h-2v-7h2v7z',
  };

  return (
    <svg aria-hidden viewBox="0 0 24 24" className="size-4 shrink-0 fill-current">
      <path d={paths[name]} />
    </svg>
  );
}
